import React, { useState } from "react";
import { MockupFrame } from "./MockupFrame";

const STAGES = [
	{
		name: "HOT",
		color: "#ef4444",
		channels: [
			{ address: "1417 Hawaii St, Fairfield, CA", props: 13, media: 16, time: "1d" },
			{ address: "1024 Mariposa St, Vallejo, CA", props: 11, media: 0, time: "3d" },
			{ address: "2054 Deerwood Dr, Martinez, CA", props: 6, media: 2, time: "" },
			{ address: "17395 De Witt Ave, Morgan Hill, CA", props: 6, media: 36, time: "9h" },
			{ address: "607 Seagull Dr, Suisun City, CA", props: 18, media: 0, time: "10h", badge: "PENDING PROJECTIONS" },
		],
	},
	{
		name: "WARM",
		color: "#f97316",
		channels: [
			{ address: "892 Maple Creek Ln, Austin, TX", props: 5, media: 12, time: "2h" },
			{ address: "3156 Elm Ridge Ct, Austin, TX", props: 8, media: 24, time: "5h" },
		],
	},
	{
		name: "ESCROW",
		color: "#a855f7",
		channels: [
			{ address: "745 Cedar Park Blvd, Cedar Park, TX", props: 3, media: 8, time: "1d" },
		],
	},
	{
		name: "ACTIVE",
		color: "#22c55e",
		channels: [
			{ address: "2081 Willow Bend Dr, Round Rock, TX", props: 4, media: 19, time: "3d" },
			{ address: "510 Oak Hollow, Georgetown, TX", props: 6, media: 31, time: "1w" },
		],
	},
	{
		name: "SOLD",
		color: "#10b981",
		channels: [
			{ address: "1290 Sunset Blvd, Pflugerville, TX", props: 5, media: 22, time: "2w" },
		],
	},
	{
		name: "ARCHIVED",
		color: "#52525b",
		channels: [
			{ address: "400 Congress Ave, Austin, TX", props: 3, media: 6, time: "1mo" },
		],
	},
];

const PIPELINE_SUMMARY = [
	{ name: "Hot", color: "#ef4444", count: 10 },
	{ name: "Warm", color: "#f97316", count: 8 },
	{ name: "Cold", color: "#3b82f6", count: 15 },
	{ name: "Escrow", color: "#a855f7", count: 3 },
	{ name: "Active", color: "#22c55e", count: 7 },
	{ name: "Sold", color: "#10b981", count: 21 },
	{ name: "Archived", color: "#52525b", count: 9 },
];

export function MockupPipeline() {
	const [collapsedStages, setCollapsedStages] = useState<Set<string>>(new Set(["SOLD", "ARCHIVED"]));
	const [activeChannel, setActiveChannel] = useState("17395 De Witt Ave, Morgan Hill, CA");
	const [hoveredStage, setHoveredStage] = useState<string | null>(null);

	const toggleStage = (name: string) => {
		setCollapsedStages((prev) => {
			const next = new Set(prev);
			if (next.has(name)) next.delete(name);
			else next.add(name);
			return next;
		});
	};

	return (
		<MockupFrame url="comps.red/pipeline">
			<div className="flex h-[400px]">
				{/* Sidebar with Stage Groups */}
				<div className="w-60 shrink-0 border-r border-white/5 bg-zinc-950/60 flex flex-col overflow-hidden">
					{/* Header */}
					<div className="flex items-center justify-between px-3 py-2.5 border-b border-white/5">
						<div className="flex items-center gap-2 cursor-pointer group">
							<div className="h-6 w-6 rounded-lg bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-[8px] font-bold text-white">
								CH
							</div>
							<span className="text-xs font-semibold text-white group-hover:text-zinc-300 transition-colors">Cyber Home Buyers</span>
							<svg className="h-3 w-3 text-zinc-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
								<path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
							</svg>
						</div>
					</div>

					{/* Tabs */}
					<div className="flex border-b border-white/5">
						<button className="flex-1 py-1.5 text-[10px] font-medium text-white border-b-2 border-[#db241e]"># Channels</button>
						<button className="flex-1 py-1.5 text-[10px] text-zinc-500 hover:text-zinc-400 transition-colors">Messages</button>
					</div>

					{/* Search */}
					<div className="px-2.5 py-2">
						<div className="rounded-md bg-zinc-800/60 px-2.5 py-1.5 text-[9px] text-zinc-600 flex items-center gap-1.5">
							<svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
								<path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
							</svg>
							Search property decks...
						</div>
					</div>

					{/* Stage Groups */}
					<div className="flex-1 overflow-y-auto">
						{STAGES.map((stage) => {
							const isCollapsed = collapsedStages.has(stage.name);
							return (
								<div key={stage.name} className="mb-0.5">
									<button
										onClick={() => toggleStage(stage.name)}
										className="flex w-full items-center gap-1.5 px-3 py-1 text-left hover:bg-white/[0.02] transition-colors"
									>
										<svg
											className={`h-3 w-3 transition-transform duration-200 ${isCollapsed ? "-rotate-90" : ""}`}
											style={{ color: stage.color }}
											fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
										>
											<path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
										</svg>
										<span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: stage.color }}>
											{stage.name}
										</span>
										<span className="text-[9px] text-zinc-600">({stage.channels.length})</span>
									</button>

									{!isCollapsed && stage.channels.map((ch) => (
										<div
											key={ch.address}
											onClick={() => setActiveChannel(ch.address)}
											className={`mx-1.5 mb-px rounded-md px-2 py-1.5 flex items-center gap-2 cursor-pointer transition-all duration-150 ${
												activeChannel === ch.address
													? "bg-white/[0.06] ring-1 ring-white/10"
													: "hover:bg-white/[0.03]"
											}`}
											style={activeChannel === ch.address ? { boxShadow: `inset 3px 0 0 0 ${stage.color}` } : undefined}
										>
											<div
												className="h-6 w-6 shrink-0 rounded-full flex items-center justify-center text-[7px] font-bold text-white/70"
												style={{ backgroundColor: `${stage.color}25`, border: `1px solid ${stage.color}35` }}
											>
												{ch.address.charAt(0)}
											</div>
											<div className="flex min-w-0 flex-1 flex-col">
												<div className="flex items-center justify-between gap-1">
													<span className={`truncate text-[9px] ${activeChannel === ch.address ? "font-semibold text-white" : "font-medium text-zinc-400"}`}>
														{ch.address}
													</span>
													{ch.time && <span className="shrink-0 text-[8px] text-zinc-600">{ch.time}</span>}
												</div>
												<div className="flex items-center gap-1 text-[8px] text-zinc-600">
													<span>{ch.props} properties</span>
													{ch.media > 0 && <><span>&middot;</span><span>&#128247;{ch.media}</span></>}
												</div>
												{ch.badge && (
													<span className="mt-0.5 inline-block w-fit rounded px-1 py-0.5 text-[6px] font-bold uppercase bg-orange-500/20 text-orange-400">
														{ch.badge}
													</span>
												)}
											</div>
										</div>
									))}

									{isCollapsed && (
										<div className="mx-3 py-0.5 text-[8px] text-zinc-600 italic">
											{stage.channels.length} deck{stage.channels.length !== 1 ? "s" : ""} hidden
										</div>
									)}
								</div>
							);
						})}
					</div>
				</div>

				{/* Pipeline Visualization */}
				<div className="flex-1 bg-zinc-900/40 p-5 overflow-hidden flex flex-col">
					<div className="mb-4">
						<h3 className="text-sm font-semibold text-white">Deal Pipeline Overview</h3>
						<p className="text-[10px] text-zinc-500 mt-0.5">73 properties across 7 stages &middot; Drag decks between stages</p>
					</div>

					{/* Pipeline Flow */}
					<div className="flex items-center gap-1.5 mb-5 overflow-x-auto pb-2">
						{PIPELINE_SUMMARY.map((stage, i) => (
							<React.Fragment key={stage.name}>
								<div
									className="flex flex-col items-center gap-1.5 min-w-[72px] cursor-pointer group"
									onMouseEnter={() => setHoveredStage(stage.name)}
									onMouseLeave={() => setHoveredStage(null)}
								>
									<div
										className={`flex h-12 w-12 items-center justify-center rounded-xl text-sm font-bold text-white transition-all duration-200 ${
											hoveredStage === stage.name ? "scale-110" : ""
										}`}
										style={{
											backgroundColor: stage.color,
											boxShadow: hoveredStage === stage.name ? `0 4px 16px ${stage.color}55` : undefined,
										}}
									>
										{stage.count}
									</div>
									<span className={`text-[9px] font-medium text-center whitespace-nowrap transition-colors ${
										hoveredStage === stage.name ? "text-zinc-300" : "text-zinc-500"
									}`}>
										{stage.name}
									</span>
								</div>
								{i < PIPELINE_SUMMARY.length - 1 && (
									<svg className="h-3 w-4 shrink-0 text-zinc-700" viewBox="0 0 16 12" fill="none">
										<path d="M1 6h12m0 0l-4-4m4 4l-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
									</svg>
								)}
							</React.Fragment>
						))}
					</div>

					{/* Recent Activity */}
					<div className="flex-1 rounded-lg border border-white/5 overflow-hidden">
						<div className="px-3 py-2 border-b border-white/5 bg-zinc-800/30">
							<span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Recent Stage Changes</span>
						</div>
						<div className="divide-y divide-white/5">
							{[
								{ deck: "Austin Renovation Deals", from: "Cold", to: "Hot", fromColor: "#3b82f6", toColor: "#ef4444", time: "2h ago" },
								{ deck: "Cedar Park Flips Q1", from: "Hot", to: "Escrow", fromColor: "#ef4444", toColor: "#a855f7", time: "1d ago" },
								{ deck: "Round Rock Comps", from: "Warm", to: "Active", fromColor: "#f97316", toColor: "#22c55e", time: "3d ago" },
							].map((change, i) => (
								<div key={i} className="px-3 py-2 flex items-center gap-2 hover:bg-white/[0.02] cursor-pointer transition-colors">
									<div className="min-w-0 flex-1">
										<p className="text-[10px] font-medium text-white truncate">{change.deck}</p>
										<div className="flex items-center gap-1 mt-0.5">
											<span className="text-[8px] font-bold px-1 rounded" style={{ color: change.fromColor, backgroundColor: `${change.fromColor}15` }}>
												{change.from}
											</span>
											<svg className="h-2.5 w-2.5 text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
												<path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
											</svg>
											<span className="text-[8px] font-bold px-1 rounded" style={{ color: change.toColor, backgroundColor: `${change.toColor}15` }}>
												{change.to}
											</span>
										</div>
									</div>
									<span className="text-[8px] text-zinc-600 shrink-0">{change.time}</span>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		</MockupFrame>
	);
}
